import clsx from 'clsx';
import React, { ReactNode } from 'react';

interface IntegrationTileProps {
  icon: ReactNode;
  iconClassName?: string;
  title: string;
  description: string;
  connected: boolean;
  // Reserved for the one tile that deserves the accent treatment (shadow-glow), see CLAUDE.md
  // styling conventions — sparing use only.
  highlight?: boolean;
  badge?: ReactNode; // overrides the default Connected/Not connected badge, e.g. MCP's "New"
  onClick: () => void;
}

// Compact clickable card for the Integrations page grid — icon, title, one-line description, and
// a status badge only. Clicking opens the integration's full detail/connect UI in a modal
// (IntegrationCard). Kept intentionally light so ~10 integrations stay scannable at a glance
// instead of each one's full connect UI competing for space on the page at once.
const IntegrationTile: React.FC<IntegrationTileProps> = ({
  icon,
  iconClassName,
  title,
  description,
  connected,
  highlight,
  badge,
  onClick,
}) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => e.key === 'Enter' && onClick()}
    className={clsx(
      'card bg-base-100 text-left cursor-pointer hover:-translate-y-0.5 transition',
      highlight ? 'shadow-glow bg-primary/5' : 'shadow-card',
    )}
  >
    <div className="card-body p-4 gap-3">
      <div className="flex items-start justify-between gap-2">
        <div
          className={clsx(
            'shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-xl bg-base-200',
            iconClassName,
          )}
        >
          {icon}
        </div>
        {badge ?? (
          <span className={clsx('badge badge-sm shrink-0', connected ? 'badge-success' : 'badge-ghost')}>
            {connected ? 'Connected' : 'Not connected'}
          </span>
        )}
      </div>
      <div className="min-w-0">
        <div className="font-semibold text-sm">{title}</div>
        <div className="text-xs opacity-60 line-clamp-2">{description}</div>
      </div>
    </div>
  </div>
);

export default IntegrationTile;
